import "./App.css";
// @ts-ignore
import { AppConst, AppRoute } from "./common/app-constants.ts";
import AppLogo from "./ui-components/app-logo";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import BoltIcon from "@mui/icons-material/Bolt";
import SecurityIcon from "@mui/icons-material/Security";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailIcon from "@mui/icons-material/Email";

const CONTACT_EMAIL = "info@substring.org";

const features = [
  {
    icon: <BoltIcon sx={{ fontSize: 40 }} />,
    title: "Instant Payments",
    description:
      "Accept payments in seconds with lightning-fast processing powered by modern infrastructure.",
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: "Stripe-Powered Security",
    description:
      "Enterprise-grade security backed by Stripe, the world's most trusted payment processor.",
  },
  {
    icon: <DashboardIcon sx={{ fontSize: 40 }} />,
    title: "Business Dashboard",
    description:
      "Monitor your sales, track orders, and manage your business from one intuitive dashboard.",
  },
  {
    icon: <ShoppingCartCheckoutIcon sx={{ fontSize: 40 }} />,
    title: "Seamless Checkout",
    description:
      "Frictionless checkout experience that converts visitors into paying customers.",
  },
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
    title: "Fast Onboarding",
    description:
      "Get up and running in minutes. Connect your Stripe account and start accepting payments today.",
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 40 }} />,
    title: "Real-time Analytics",
    description:
      "Gain actionable insights with real-time data on transactions, revenue, and customer behavior.",
  },
];

const benefits = [
  {
    icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
    title: "Grow Revenue Faster",
    description:
      "Our optimized checkout flows and payment processing help you capture more sales and reduce cart abandonment.",
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 36 }} />,
    title: "Trusted & Secure",
    description:
      "Built on Stripe's world-class infrastructure, your transactions and customer data are always protected.",
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 36 }} />,
    title: "Built for Businesses",
    description:
      "Whether you're a solo entrepreneur or a growing team, DashGifts scales with your business needs.",
  },
];

// Shared style for gradient CTA buttons
const gradientButtonSx = {
  background: "linear-gradient(135deg, #1976d2 0%, #ab47bc 50%, #ec407a 100%)",
  backgroundSize: "200% 200%",
  animation: "gradient-shift 4s ease infinite",
  color: "#fff",
  fontWeight: 700,
  fontSize: { xs: "1rem", md: "1.1rem" },
  px: 5,
  py: 1.8,
  borderRadius: "50px",
  textTransform: "none",
  letterSpacing: "0.5px",
  boxShadow: "0 4px 24px rgba(171, 71, 188, 0.4)",
  "&:hover": {
    boxShadow: "0 6px 32px rgba(171, 71, 188, 0.6)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
};

function App() {
  return (
    <div className="App">
      {/* ==================== HEADER ==================== */}
      <Box
        component="header"
        sx={{
          py: 2,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 10,
        }}
      >
        <AppLogo />
      </Box>

      {/* ==================== HERO SECTION ==================== */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 16 },
        }}
      >
        {/* Decorative gradient orb - top right */}
        <Box
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-5%",
            width: { xs: "300px", md: "500px" },
            height: { xs: "300px", md: "500px" },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(171,71,188,0.15) 0%, transparent 70%)",
            animation: "glow-pulse 6s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        {/* Decorative gradient orb - bottom left */}
        <Box
          sx={{
            position: "absolute",
            bottom: "-15%",
            left: "-10%",
            width: { xs: "250px", md: "450px" },
            height: { xs: "250px", md: "450px" },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(25,118,210,0.12) 0%, transparent 70%)",
            animation: "glow-pulse 8s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2.2rem", sm: "3rem", md: "3.8rem" },
              lineHeight: 1.15,
              mb: 3,
              color: "#ffffff",
            }}
          >
            The Modern Payment Platform for{" "}
            <Box component="span" className="gradient-text">
              Growing Businesses
            </Box>
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 400,
              fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.35rem" },
              lineHeight: 1.6,
              mb: 5,
              color: "rgba(255,255,255,0.7)",
              maxWidth: "640px",
              mx: "auto",
            }}
          >
            {AppConst.APP_NAME} is a payment processing platform that helps
            businesses accept payments seamlessly via Stripe. Sell more, grow
            faster, and delight your customers.
          </Typography>

          <Button
            component="a"
            href={`mailto:${CONTACT_EMAIL}`}
            variant="contained"
            size="large"
            sx={gradientButtonSx}
          >
            Get Started
          </Button>
        </Container>
      </Box>

      {/* ==================== FEATURES GRID ==================== */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              mb: 2,
              color: "#ffffff",
            }}
          >
            Everything You Need to{" "}
            <Box component="span" className="gradient-text">
              Accept Payments
            </Box>
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontSize: { xs: "1rem", md: "1.15rem" },
              mb: 6,
              maxWidth: "560px",
              mx: "auto",
            }}
          >
            Powerful features designed to help your business collect payments,
            track revenue, and grow.
          </Typography>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "16px",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.35s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      border: "1px solid rgba(171,71,188,0.3)",
                      boxShadow: "0 8px 32px rgba(171,71,188,0.15)",
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(135deg, rgba(25,118,210,0.2) 0%, rgba(171,71,188,0.2) 100%)",
                        mb: 2.5,
                        mx: "auto",
                        color: "#4fc3f7",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        mb: 1.2,
                        color: "#ffffff",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        fontSize: "0.92rem",
                        lineHeight: 1.65,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== WHY DASHGIFTS ==================== */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background:
            "linear-gradient(180deg, rgba(15,52,96,0.3) 0%, rgba(26,26,46,0.5) 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              mb: 2,
              color: "#ffffff",
            }}
          >
            Why{" "}
            <Box component="span" className="gradient-text">
              {AppConst.APP_NAME}
            </Box>
            ?
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontSize: { xs: "1rem", md: "1.15rem" },
              mb: 7,
              maxWidth: "520px",
              mx: "auto",
            }}
          >
            We make it simple for businesses to accept payments and focus on
            what matters most — growing.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Stack
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "16px",
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg, #1976d2 0%, #ab47bc 100%)",
                      color: "#ffffff",
                      boxShadow: "0 4px 20px rgba(171,71,188,0.3)",
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#ffffff", fontSize: "1.15rem" }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "0.95rem",
                      lineHeight: 1.65,
                      textAlign: "center",
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ==================== SOCIAL PROOF / TRUST ==================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.5rem", md: "2rem" },
              mb: 3,
              color: "#ffffff",
            }}
          >
            Trusted by Businesses Worldwide
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontSize: { xs: "1rem", md: "1.1rem" },
              mb: 5,
              maxWidth: "500px",
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            {AppConst.APP_NAME} leverages Stripe's world-class payment
            infrastructure to provide businesses with a reliable, secure, and
            seamless payment experience.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={4}
            justifyContent="center"
            alignItems="center"
          >
            {/* Powered by Stripe badge */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 4,
                py: 2,
                borderRadius: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <SecurityIcon sx={{ color: "#4fc3f7", fontSize: 28 }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: "1rem",
                }}
              >
                Powered by Stripe
              </Typography>
            </Box>

            {/* Secure payments badge */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 4,
                py: 2,
                borderRadius: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <VerifiedIcon sx={{ color: "#66bb6a", fontSize: 28 }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: "1rem",
                }}
              >
                Secure Payments
              </Typography>
            </Box>

            {/* Global reach badge */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 4,
                py: 2,
                borderRadius: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <GroupsIcon sx={{ color: "#ffa726", fontSize: 28 }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: "1rem",
                }}
              >
                Global Reach
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ==================== CONTACT CTA ==================== */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Gradient overlay background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.15) 0%, rgba(171,71,188,0.2) 50%, rgba(236,64,122,0.15) 100%)",
            zIndex: 0,
          }}
        />
        {/* Subtle top and bottom borders */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(171,71,188,0.4) 50%, transparent 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(171,71,188,0.4) 50%, transparent 100%)",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.8rem" },
              mb: 2.5,
              color: "#ffffff",
            }}
          >
            Ready to Grow Your Business?
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: { xs: "1rem", md: "1.15rem" },
              mb: 5,
              maxWidth: "480px",
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Get in touch with our team and start accepting payments with{" "}
            {AppConst.APP_NAME} today.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              component="a"
              href={`mailto:${CONTACT_EMAIL}`}
              variant="contained"
              size="large"
              startIcon={<EmailIcon />}
              sx={gradientButtonSx}
            >
              Contact Us
            </Button>
          </Stack>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.9rem",
              mt: 3,
            }}
          >
            {CONTACT_EMAIL}
          </Typography>
        </Container>
      </Box>

      {/* ==================== FOOTER ==================== */}
      <Box
        component="footer"
        sx={{
          py: 5,
          px: 3,
          background: "rgba(10,10,20,0.6)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={2.5} alignItems="center">
            <AppLogo />

            <Typography
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.9rem",
                maxWidth: "400px",
                lineHeight: 1.6,
              }}
            >
              {AppConst.APP_NAME} is a payment processing platform that helps
              businesses accept payments seamlessly via Stripe.
            </Typography>

            <Divider
              sx={{
                width: "60px",
                borderColor: "rgba(255,255,255,0.1)",
              }}
            />

            <Stack direction="row" spacing={3}>
              <Typography
                component="a"
                href={AppRoute.PRIVACY_POLICY}
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  "&:hover": { color: "#4fc3f7" },
                  transition: "color 0.2s ease",
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                component="a"
                href={AppRoute.TERMS}
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  "&:hover": { color: "#4fc3f7" },
                  transition: "color 0.2s ease",
                }}
              >
                Terms &amp; Conditions
              </Typography>
            </Stack>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.35)",
                fontSize: "0.8rem",
                mt: 1,
              }}
            >
              &copy; 2026 {AppConst.APP_NAME}. All rights reserved.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </div>
  );
}

export default App;
